import { collator, dateTimeFormatter, displayNames, numberFormatter, relativeTimeFormatter } from './intlCache'

export interface SurfaceFormatOptions {
  /** Use the existing business time zone; explicit undefined preserves runtime-local time. */
  readonly timeZone: string | undefined
}

export type DateFormatOptions = Intl.DateTimeFormatOptions & SurfaceFormatOptions
export type DateValue = Date | number

const dateFields = { year: 'numeric', month: '2-digit', day: '2-digit' } as const
const timeFields = { hour: '2-digit', minute: '2-digit' } as const
const dateComponents = ['weekday', 'era', 'year', 'month', 'day'] as const
const timeComponents = ['dayPeriod', 'hour', 'minute', 'second', 'fractionalSecondDigits'] as const
const dateTimeComponents = [...dateComponents, ...timeComponents]

function hasExplicitFormat(options: DateFormatOptions, components: readonly (keyof Intl.DateTimeFormatOptions)[]): boolean {
  return options.dateStyle !== undefined || options.timeStyle !== undefined
    || components.some((key) => options[key] !== undefined)
}

export function formatDate(culture: string, value: DateValue, options: DateFormatOptions): string {
  return dateTimeFormatter(culture, { ...(hasExplicitFormat(options, dateComponents) ? {} : dateFields), ...options }).format(value)
}

export function formatTime(culture: string, value: DateValue, options: DateFormatOptions): string {
  return dateTimeFormatter(culture, { ...(hasExplicitFormat(options, timeComponents) ? {} : timeFields), ...options }).format(value)
}

export function formatDateTime(culture: string, value: DateValue, options: DateFormatOptions): string {
  const fields = hasExplicitFormat(options, dateTimeComponents) ? {} : { ...dateFields, ...timeFields }
  return dateTimeFormatter(culture, { ...fields, ...options }).format(value)
}

export function formatNumber(culture: string, value: number | bigint, options: Intl.NumberFormatOptions = {}): string {
  return numberFormatter(culture, options).format(value)
}

/** Localize decimal transport text without rounding or converting it to a JS float. */
export function formatDecimalText(culture: string, value: string): string {
  if (!/^-?\d+(?:\.\d+)?$/.test(value)) return value
  const formatter = numberFormatter(culture, { useGrouping: false })
  const decimal = formatter.formatToParts(1.1).find(part => part.type === 'decimal')!.value
  const minus = formatter.formatToParts(-1).find(part => part.type === 'minusSign')!.value
  return value.replace(/\d/g, digit => formatter.format(Number(digit))).replace('.', decimal).replace('-', minus)
}

/** Display names follow the interface language, independently of numeric culture. */
export function formatDisplayName(language: string, code: string, type: Intl.DisplayNamesType): string | undefined {
  return displayNames(language, { type }).of(code)
}

export function formatNumberParts(culture: string, value: number | bigint, options: Intl.NumberFormatOptions = {}): Intl.NumberFormatPart[] {
  return numberFormatter(culture, options).formatToParts(value)
}

/** Region labels follow the interface language, independently of numeric culture. */
export function regionName(language: string, code: string): string {
  return formatDisplayName(language, code, 'region') ?? code
}

export function uppercaseText(culture: string, value: string): string {
  return value.toLocaleUpperCase(culture)
}

/**
 * Technical decision: I/ı/İ/i form one search class in every interface language.
 * This broadens the old Turkish lowercase matches; it never removes a match
 * (for example, the query "ı" also matches "Çiğdem"). Other letters keep their
 * Turkish lowercase behavior; this is not accent stripping or display casing.
 */
export function foldTurkishSearch(value: string): string {
  return foldForSearch('tr', value.replace(/[Iıİi]/g, 'i'))
}

export function formatInteger(culture: string, value: number, options: Intl.NumberFormatOptions = {}): string {
  return formatNumber(culture, value, { ...options, minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

export function formatPercent(culture: string, value: number, options: Intl.NumberFormatOptions = {}): string {
  return formatNumber(culture, value, { ...options, style: 'percent' })
}

export function formatCurrency(culture: string, value: number, currency: string, options: Intl.NumberFormatOptions = {}): string {
  return formatNumber(culture, value, { ...options, style: 'currency', currency })
}

export function formatRelativeTime(culture: string, value: number, unit: Intl.RelativeTimeFormatUnit, options: Intl.RelativeTimeFormatOptions = {}): string {
  return relativeTimeFormatter(culture, options).format(value, unit)
}

export function compareText(culture: string, a: string, b: string, options: Intl.CollatorOptions = {}): number {
  return collator(culture, options).compare(a, b)
}

export function foldForSearch(culture: string, value: string): string {
  return value.toLocaleLowerCase(culture)
}

/** Bind to a surface getter, never a process-wide mutable locale or a stale snapshot. */
export function createSurfaceFormatters(culture: () => string, options: SurfaceFormatOptions) {
  const dateOptions = (overrides: Intl.DateTimeFormatOptions): DateFormatOptions => ({ ...overrides, timeZone: overrides.timeZone ?? options.timeZone })
  return {
    formatDate: (value: DateValue, overrides: Intl.DateTimeFormatOptions = {}) => formatDate(culture(), value, dateOptions(overrides)),
    formatTime: (value: DateValue, overrides: Intl.DateTimeFormatOptions = {}) => formatTime(culture(), value, dateOptions(overrides)),
    formatDateTime: (value: DateValue, overrides: Intl.DateTimeFormatOptions = {}) => formatDateTime(culture(), value, dateOptions(overrides)),
    formatNumber: (value: number, overrides?: Intl.NumberFormatOptions) => formatNumber(culture(), value, overrides),
    formatDecimalText: (value: string) => formatDecimalText(culture(), value),
    formatInteger: (value: number, overrides?: Intl.NumberFormatOptions) => formatInteger(culture(), value, overrides),
    formatPercent: (value: number, overrides?: Intl.NumberFormatOptions) => formatPercent(culture(), value, overrides),
    formatCurrency: (value: number, currency: string, overrides?: Intl.NumberFormatOptions) => formatCurrency(culture(), value, currency, overrides),
    formatRelativeTime: (value: number, unit: Intl.RelativeTimeFormatUnit, overrides?: Intl.RelativeTimeFormatOptions) => formatRelativeTime(culture(), value, unit, overrides),
    compareText: (a: string, b: string, overrides?: Intl.CollatorOptions) => compareText(culture(), a, b, overrides),
    foldForSearch: (value: string) => foldForSearch(culture(), value),
  }
}
